import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { RiskLevelSliderV4 } from '../RiskLevelSliderV4';
import { Icon } from '../Icon';
import { RISK_CONFIG, calculateRiskScore, getRiskLevel } from '../../shared/risk-helpers';
import styles from './ProfileSlideV6.css';

const SLIDER_LEVELS = ['low', 'moderate', 'high'];

const ITEMS = [
    {
        id: 4,
        name: 'Online Fraud & Scam Risk',
        iconName: 'link',
        color: 'var(--error-light-color)',
        status: 'Unprotected'
    },
    {
        id: 3,
        name: 'Private Data Theft Risk',
        iconName: 'archive',
        color: 'var(--error-light-color)',
        status: 'Action Required'
    },
    {
        id: 1,
        name: 'Identity Theft Risk',
        iconName: 'userSquare',
        color: 'var(--error-light-color)',
        status: 'Unsecured'
    },
    {
        id: 2,
        name: 'Financial Theft Risk',
        iconName: 'creditCard',
        color: 'var(--warning-color)',
        status: 'Needs Monitoring'
    }
];

function ProfileSlideV6({
    meters = [],
    results = {}
}) {
    const userResponses = results.userResponses || [];
    const meterData = meters.map(meter => {
        const score = Math.min(calculateRiskScore(userResponses, meter.questionIds), meter.maxScore);
        const percent = Math.round((score / meter.maxScore) * 100);
        const color = RISK_CONFIG[getRiskLevel(score, meter.scoreThresholds)].color;

        return { ...meter, score, percent, color };
    });

    const [animatedPercents, setAnimatedPercents] = useState(() => meterData.map(() => 0));

    const setPercentAt = (index, percent) => setAnimatedPercents(prev => prev.map((value, valueIndex) => (valueIndex === index ? percent : value)));

    useEffect(() => {
        const timers = meterData.map((meter, index) => setTimeout(() => setPercentAt(index, meter.percent), 50 + index * 150));

        return () => timers.forEach(clearTimeout);
    }, []);

    return (
        <div className={styles.root}>
            <h2 className={`${styles.title} main-title`}>Your Digital Risk Profile</h2>
            <div className={styles.informationCard}>
                {meterData.map((meter, index) => (
                    <div className={styles.informationCardItem} key={meter.label}>
                        <div className={styles.level}>
                            <p>{meter.label}</p>
                            <p
                                className={styles.score}
                                style={{
                                    color: meter.color,
                                    backgroundColor: `color-mix(in srgb, ${meter.color} 6%, #fff)`,
                                    borderColor: `color-mix(in srgb, ${meter.color} 20%, #fff)`
                                }}
                            >
                                {meter.score}/{meter.maxScore}
                            </p>
                        </div>
                        <RiskLevelSliderV4
                            riskPercent={animatedPercents[index]}
                            riskColor={meter.color}
                            levels={SLIDER_LEVELS}
                        />
                    </div>
                ))}
            </div>
            <ul className={styles.list}>
                {ITEMS.map(item => (
                    <li
                        className={styles.listItem}
                        style={{ color: item.color }}
                        key={item.id}
                    >
                        <div className={styles.iconWrapper}>
                            <Icon name={item.iconName} className={styles.icon} />
                        </div>
                        <div>
                            <p className={styles.litItemTitle}>{item.name}</p>
                            <p className={styles.listItemStatus}>{item.status}</p>
                        </div>
                    </li>
                ))}
            </ul>
            <p className={styles.disclaimer}>For illustrative purposes only</p>
        </div>
    );
}

ProfileSlideV6.propTypes = {
    meters: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        maxScore: PropTypes.number.isRequired,
        questionIds: PropTypes.arrayOf(PropTypes.number).isRequired,
        scoreThresholds: PropTypes.shape({
            low: PropTypes.number.isRequired,
            moderate: PropTypes.number.isRequired
        }).isRequired
    })),
    results: PropTypes.shape({
        userResponses: PropTypes.array
    })
};

export {
    ProfileSlideV6
};
